'use client';

import { CbShadowSubtle } from '@coolblue-development/design-tokens';
import {
  Button,
  Chip,
  freddyTheme,
  Modal,
  ModalBody,
  ModalFooter
} from '@coolblue-development/freddy';
import selectChooseLocation from '@coolblue-development/ga4-events/triggers/VacancyBanner/selectChooseLocationTrigger';
import selectShowVacanciesTrigger from '@coolblue-development/ga4-events/triggers/VacancyLocationSelector/selectShowVacanciesTrigger';
import { ArrowDown, PinOutlined } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { useTranslation } from '@i18n/useTranslation';
import type { Locations } from '@sharedTypes';
import { generateWebsiteUrl } from '@utils/url-generators/generateWebsiteUrl';
import Link from 'next/link';
import { useState } from 'react';

import { LocationSelectorModalBody } from '.';

type Props = {
  locations: Locations;
};

export const LocationSelector = ({ locations }: Props) => {
  const { t } = useTranslation('vacancyDetail.header.locationSelector');
  const modalTranslations = useFreddyTranslations('modal');
  const { subsidiary, language, environment } = useRequestContext();

  const { trackEvent } = useAnalytics();

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [isError, setIsError] = useState(false);

  return (
    <>
      <Button
        onClick={() => {
          setIsOpen(true);
          trackEvent(selectChooseLocation());
        }}
        sx={{
          all: 'unset',
          position: 'relative',
          '&:hover, &:focus': {
            all: 'unset',
            cursor: 'pointer'
          },
          '.chevron': {
            color: freddyTheme.color.font.interactiveSubtle
          }
        }}
      >
        <Chip
          startIcon={<PinOutlined />}
          sx={{ boxShadow: CbShadowSubtle, paddingRight: '3.6rem' }}
          value='location'
        >
          {t('chooseLocation')}
          <ArrowDown className='chevron' />
        </Chip>
      </Button>
      <Modal
        translations={modalTranslations}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('title')}
        size='medium'
        body={
          <ModalBody>
            <LocationSelectorModalBody
              value={value}
              onSelectLocation={location => {
                setIsError(false);
                setValue(location);
              }}
              locations={locations}
              isError={isError}
            />
          </ModalBody>
        }
        footer={
          <ModalFooter>
            <Link
              onClick={() => {
                setIsError(value === '');
                trackEvent(
                  selectShowVacanciesTrigger({ status: value === '' ? 'failure' : 'success' })
                );
              }}
              href={generateWebsiteUrl(value, subsidiary, language, environment)}
              style={{ width: '100%' }}
            >
              <Button component='div' fullWidth>
                {t('goToVacancy')}
              </Button>
            </Link>
          </ModalFooter>
        }
      />
    </>
  );
};
